import axios from 'axios'
import {debounce} from 'lodash'
import {useCallback, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {Tooltip} from 'react-tooltip'
import {PageTitle} from '../../../../_metronic/layout/core'
import DateTimeComp from '../../../../components/dateComponent/DateTimeComp'
import {LoadingComponent} from '../../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../../components/noRecord/NoRecordFound'
import Pagination from '../../../../components/pagination/Pagination'
import SearchBarComponent from '../../../../components/searchBar/SearchBarComponent'
import TableHeading from '../../../../components/tableHeading/TableHeading'
import TableSettingMenu from '../../../../components/tableHeading/TableSettingMenu'
import TextField from '../../../../components/textFields/TextField'
import {selectAuth} from '../../../../features/authSlice'
import {useAppSelector} from '../../../../hooks'
import {useGetCCFeedfilesQuery} from '../../../../services/FilesStorageApi'
import labelKey from '../../../localization/label.json'
import tosterKey from '../../../localization/toster.json'
import BankCCFeedDetailViewModal from './BankCCFeedDetailViewModal'
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent'

interface BankCCFeedFilesProps {
  removeRoundedTop?: boolean
  showImportButtons?: boolean
}

const BankCCFeedFiles = ({
  removeRoundedTop = false,
  showImportButtons = false,
}: BankCCFeedFilesProps) => {
  const {token, baseURL, userInOrgId} = useAppSelector(selectAuth)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [searchStr, setSearchStr] = useState('') // debounce search
  const [immediateSearchStr, setImmediateSearchStr] = useState('')
  const [sortBy, setSortBy] = useState('createDT')
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortedColumn, setSortedColumn] = useState('createDT')
  const [filterByStatus, setFilterByStatus] = useState('')

  // Debounced search state
  // const [searchStr, setSearchStr] = useState('');

  const {
    data,
    isFetching: isLoading,
    isSuccess,
    refetch,
  } = useGetCCFeedfilesQuery({
    pageNo: pageNumber,
    pageSize: pageSize,
    orgId: userInOrgId,
  })

  const handlePageSizeChange = (e: any) => {
    const newSize = parseInt(e.target.value)
    setPageSize(newSize)
    setPageNumber(1) // Reset page number to 1
  }
  // pagination
  const handlePageClick = (e: any) => {
    setPageNumber(e.selected + 1)
  }
  //per page record
  const handlePageRecords = (e: any) => {
    setPageNumber(1) // Reset the page to 1
  }
  const handleSort = (property: string) => {
    setSortBy(property)
    if (sortedColumn === property) {
      setSortOrder((prevSortOrder) => (prevSortOrder === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortedColumn(property)
      setSortOrder('asc')
    }
  }

  const handleSearchChange = (e: any) => {
    const {value} = e.target
    setImmediateSearchStr(value) // Update immediate search state

    delayedSearch(value) // Update debounced search state
    setPageNumber(1)
  }

  const delayedSearch = useCallback(
    debounce((searchValue) => {
      setSearchStr(searchValue)
    }, 500),
    []
  )

  useEffect(() => {
    if (searchStr !== '' && sortOrder !== '' && sortBy !== '') {
      // Trigger the API call after the debounce time
      refetch()
    }
  }, [searchStr, sortOrder, sortBy, refetch])
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([
    'fileId',
    'fileName',
    'createdDate',
    'viewCopyURL',
    'actions',
  ])
  const handleToggleColumn = (columnId: string) => {
    if (hiddenColumns.includes(columnId)) {
      setHiddenColumns(hiddenColumns.filter((id) => id !== columnId))
    } else {
      setHiddenColumns([...hiddenColumns, columnId])
    }
  }

  const [showBankFeedDetailModal, setShowBankFeedDetailModal] = useState(false)
  const [bankFeedData, setBankFeedData] = useState()

  const bankFeedDetailHandler = () => {
    setShowBankFeedDetailModal((prev) => !prev)
  }

  const handleFileDownload = async (fileId: string | number, fileName: string) => {
    try {
      const getExpenseLogResponse = await axios.get(
        `${baseURL}/api/FilesStorage/DownloadCCFeedFile?orgId=${userInOrgId}&fileId=${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        }
      )
      const blob = new Blob([getExpenseLogResponse.data], {type: 'application/octet-stream'})
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName ?? '')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully!');
    } catch (error) {
      alert('Failed to download file. Please try again.')
    }
  }

  useEffect(() => {
    if (showBankFeedDetailModal === false) {
      refetch()
    }
  }, [showBankFeedDetailModal])

  const handleRefresh = () => {
    refetch().then(() => {
      if (isSuccess) {
        toast.success(tosterKey.dataRefreshedSuccesfully)
      }
    })
  }

  return (
    <>
      <BankCCFeedDetailViewModal
        show={showBankFeedDetailModal}
        handleClose={() => setShowBankFeedDetailModal(false)}
        bankFeedData={bankFeedData}
      />
      <PageTitle breadcrumbs={[]}>{labelKey.automationBankCCFeedFiles}</PageTitle>

      <div className={`card mb-5 ${removeRoundedTop ? 'rounded-top-0' : ''}`}>
        <div className='card-header border-0 pt-6'>
          <div className='d-flex align-items-center '>
            <Tooltip id='refreshData' place='top' />
            <RefreshComponent onClick={handleRefresh} />
          </div>
        </div>
        <div className='card-body py-8'>
          <div className='col-md-12'>
            <div className='table-responsive table-height'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    {hiddenColumns.includes('fileId') && (
                      <TableHeading label={labelKey.fileId} columnId='fileId' className='w-400px' />
                    )}
                    {hiddenColumns.includes('fileName') && (
                      <TableHeading
                        label={labelKey.fileName}
                        columnId='fileName'
                        className='w-400px'
                      />
                    )}
                    {hiddenColumns.includes('viewCopyURL') && (
                      <TableHeading label={labelKey.viewCopyURL} columnId='viewCopyURL' />
                    )}
                    {hiddenColumns.includes('createdDate') && (
                      <TableHeading label={labelKey.createdDate} columnId='createdDate' />
                    )}
                    <th className='text-end rounded-end pe-2 border-0'>
                      {hiddenColumns.includes('actions') && <>{labelKey.actions}</>}
                      <TableSettingMenu>
                        <TextField
                          rightLabel={labelKey.fileId}
                          type='checkbox'
                          onChange={() => handleToggleColumn('fileId')}
                          checked={hiddenColumns.includes('fileId')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.fileName}
                          type='checkbox'
                          onChange={() => handleToggleColumn('fileName')}
                          checked={hiddenColumns.includes('fileName')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.viewCopyURL}
                          type='checkbox'
                          onChange={() => handleToggleColumn('viewCopyURL')}
                          checked={hiddenColumns.includes('viewCopyURL')}
                          fieldClass='mb-4'
                        />
                        <TextField
                          rightLabel={labelKey.createdDate}
                          type='checkbox'
                          onChange={() => handleToggleColumn('createdDate')}
                          checked={hiddenColumns.includes('createdDate')}
                          fieldClass='mb-4'
                        />
                      </TableSettingMenu>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading && (
                    <>
                      {data?.data?.length > 0 ? (
                        <>
                          {data?.data?.map((data: any, index: any) => (
                            <tr key={index}>
                              {hiddenColumns.includes('fileId') && <td>{data?.fileId}</td>}
                              {hiddenColumns.includes('fileName') && <td>{data?.fileName}</td>}
                              {hiddenColumns.includes('viewCopyURL') && (
                                <td className='text-truncate' style={{maxWidth: '220px'}}>
                                  <div className='d-flex align-items-center'>
                                    {/* Clickable Link */}
                                    <a
                                      href={data?.url}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='text-primary text-truncate me-2'
                                      title={data?.url} // full URL on hover
                                      style={{maxWidth: '180px', display: 'inline-block'}}
                                    >
                                      {data?.url}
                                    </a>

                                    {/* Copy Button */}
                                    <button
                                      type='button'
                                      className='btn btn-icon btn-sm btn-light'
                                      data-bs-toggle='tooltip'
                                      data-bs-placement='top'
                                      title='Copy URL'
                                      onClick={() => {
                                        navigator.clipboard.writeText(data?.url)
                                             toast.success('URL copied to clipboard!');
                                      }}
                                    >
                                      <i className='fas fa-copy'></i>
                                    </button>
                                  </div>
                                </td>
                              )}
                              {hiddenColumns.includes('createdDate') && (
                                <td>
                                  <DateTimeComp formattedDate={data?.createdOn} />
                                </td>
                              )}
                              <td className='text-end'>
                                {hiddenColumns.includes('actions') && (
                                  <>
                                    {/* <p
                                      className='text-end pe-9 text-primary mb-0'
                                      onClick={() => {
                                        setBankFeedData(data)
                                        bankFeedDetailHandler()
                                      }}
                                    >
                                      View File Data
                                    </p> */}
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-primary'
                                      onClick={() =>
                                        handleFileDownload(data?.fileId, data?.fileName)
                                      }
                                    >
                                      <i className='fas fa-download me-2'></i>
                                      Download File
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      ) : (
                        <tr>
                          <td colSpan={11}>
                            <NoRecordFound />
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                  {isLoading && (
                    <tr>
                      <td colSpan={11} className='text-center'>
                        <LoadingComponent />
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* end::Table body */}
              </table>
            </div>
            <Pagination
              totalResult={
                data?.totalRecords === 0 ? 0 : (data?.pageNumber - 1) * data?.pageSize + 1
              }
              toResult={
                data?.totalRecords === 0
                  ? 0
                  : Math.min(data?.pageNumber * data?.pageSize, data?.totalRecords)
              }
              ofResult={data?.totalRecords}
              onChange={handlePageSizeChange}
              pageSize={pageSize}
              onPageChange={handlePageClick}
              pageCount={data?.totalPages || 0}
              currentPage={pageNumber}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default BankCCFeedFiles
